// ============================================================
// Privacidade em runtime — design.md D7, D8, D14, D16
//
// Tudo aqui roda dentro do `before_send` do SDK, no browser, antes de
// qualquer requisição de rede: o dado removido nunca sai da máquina.
// ============================================================

import type { CaptureResult, CapturedNetworkRequest } from 'posthog-js';

export type PrivacyProfile = 'public-anonymous' | 'authenticated-b2b';

// ------------------------------------------------------------
// Denylist de propriedades (D7)
// ------------------------------------------------------------

/**
 * Chaves removidas de QUALQUER payload, em qualquer profundidade.
 * São os campos pessoais do consumidor final que trafegam no pedido e, desde
 * a change `whatsapp-channel`, o conteúdo da conversa do WhatsApp: texto da
 * mensagem, transcrição do áudio, `wa_id`, o número e o nome de perfil do
 * contato.
 * A taxonomia (taxonomy.ts) já impede declará-las em tempo de compilação;
 * esta lista existe para o `track()` escrito às pressas daqui a um ano.
 */
export const DENIED_PROPERTY_KEYS = [
  'name',
  'customer_name',
  'phone',
  'email',
  'address',
  'street',
  'complement',
  'neighborhood',
  'zip_code',
  'cep',
  'observation',
  'change_for',
  'picture_url',
  'message',
  'text',
  'body',
  'wa_id',
  'whatsapp_phone',
  'profile_name',
  'transcript',
] as const;

export type DeniedPropertyKey = (typeof DENIED_PROPERTY_KEYS)[number];

const DENIED = new Set<string>(DENIED_PROPERTY_KEYS);

/**
 * Subárvores de propriedades de pessoa. No perfil `authenticated-b2b`
 * elas são preservadas: o operador do painel é contraparte contratual e
 * `identify(userId, { email, name, role })` é permitido por D7 — a
 * denylist protege as propriedades de EVENTO, onde dados do consumidor
 * final poderiam vazar. No perfil `public-anonymous` nada é preservado
 * (o identify já descarta propriedades antes de chegar aqui).
 */
const PERSON_SUBTREES = new Set(['$set', '$set_once']);

export function scrubDeniedProperties<T>(value: T, preservePersonSubtrees: boolean): T {
  if (Array.isArray(value)) {
    return value.map((item) => scrubDeniedProperties(item, preservePersonSubtrees)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (DENIED.has(key)) continue;
      if (preservePersonSubtrees && PERSON_SUBTREES.has(key)) {
        result[key] = entry;
        continue;
      }
      result[key] = scrubDeniedProperties(entry, preservePersonSubtrees);
    }
    return result as unknown as T;
  }
  return value;
}

// ------------------------------------------------------------
// Campo digitado na gravação de sessão
// ------------------------------------------------------------

// Constantes do formato de evento do rrweb (`EventType.IncrementalSnapshot`
// e `IncrementalSource.Input`), estáveis entre versões.
const RRWEB_INCREMENTAL_SNAPSHOT = 3;
const RRWEB_INPUT_SOURCE = 5;

/**
 * A gravação de sessão também passa pelo `before_send`, no evento `$snapshot`,
 * e o evento de digitação do rrweb guarda o valor do campo justamente em
 * `data.text`. A denylist apaga a chave, como deve; mas o player do replay faz
 * `campo.value = data.text`, e sem a chave o campo aparece escrito
 * "undefined". Aqui a chave volta vazia: o campo aparece em branco, e nada do
 * que foi digitado (que o `maskAllInputs` já trocava por asteriscos) sai.
 * Recebe a cópia que `scrubDeniedProperties` montou, e por isso altera no
 * lugar.
 */
function blankRecordedInputs(snapshotData: unknown): void {
  if (!Array.isArray(snapshotData)) return;
  for (const recorded of snapshotData) {
    if (recorded === null || typeof recorded !== 'object') continue;
    const { type, data } = recorded as { type?: unknown; data?: unknown };
    if (type !== RRWEB_INCREMENTAL_SNAPSHOT || data === null || typeof data !== 'object') continue;
    const input = data as Record<string, unknown>;
    if (input['source'] === RRWEB_INPUT_SOURCE && !('text' in input)) {
      input['text'] = '';
    }
  }
}

// ------------------------------------------------------------
// Normalização de URL (D16)
// ------------------------------------------------------------

// UUID v4 e afins: produto, pedido, item de catálogo do painel.
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Código público de tracking: "VPT-" + [A-Z0-9] (TrackingCodeGenerator).
const TRACKING_CODE_SEGMENT = /^vpt-[a-z0-9]{4,}$/i;
// Tokens hexadecimais longos (convites, reset de senha).
const HEX_TOKEN_SEGMENT = /^[0-9a-f]{16,}$/i;
// Token opaco genérico: longo, com dígito — não casa com slugs nem com
// os segmentos estáticos de rota, que são palavras curtas sem dígitos.
const OPAQUE_TOKEN_SEGMENT = /^(?=.*\d)[a-z0-9_-]{16,}$/i;

function isOpaqueIdentifier(segment: string): boolean {
  return (
    UUID_SEGMENT.test(segment) ||
    TRACKING_CODE_SEGMENT.test(segment) ||
    HEX_TOKEN_SEGMENT.test(segment) ||
    OPAQUE_TOKEN_SEGMENT.test(segment)
  );
}

/**
 * Segmentos estáticos cujo segmento seguinte é sempre um código, qualquer que
 * seja o formato: o código do carrinho montado pelo atendente (change
 * customer-agent-tools) é base32 e pode sair sem nenhum dígito, e o padrão de
 * token opaco não o pegaria. Vale na página (`/carrinho-pronto/{código}`) e na
 * requisição do resgate gravada pelo replay
 * (`/menu/{slug}/cart-handoffs/{código}/redeem`), sem diferenciar maiúsculas,
 * como o React Router casa a rota.
 */
const CODE_PARENT_SEGMENT = /^(carrinho-pronto|cart-handoffs)$/i;

/**
 * Substitui segmentos identificadores por `:id` e, no modo path-based do
 * cardápio, remove o primeiro segmento quando ele é o slug do tenant —
 * produzindo o mesmo caminho canônico do modo por subdomínio.
 */
export function normalizePathname(
  pathname: string,
  tenantSlug: string | null,
  slugInPath: boolean,
): string {
  const segments = pathname.split('/').filter(Boolean);
  const normalized: string[] = [];
  segments.forEach((segment, index) => {
    if (
      index === 0 &&
      slugInPath &&
      tenantSlug &&
      segment.toLowerCase() === tenantSlug.toLowerCase()
    ) {
      return;
    }
    normalized.push(
      isOpaqueIdentifier(segment) || CODE_PARENT_SEGMENT.test(segments[index - 1]) ? ':id' : segment,
    );
  });
  return `/${normalized.join('/')}`;
}

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * Normaliza uma URL (absoluta ou relativa): descarta query string e
 * fragmento, e aplica `normalizePathname` ao caminho. Em caso de falha
 * de parse, devolve apenas o caminho sem query — nunca a URL original.
 */
export function normalizeUrl(
  raw: string,
  tenantSlug: string | null,
  slugInPath: boolean,
): string {
  const stripped = raw.split(/[?#]/, 1)[0];
  try {
    if (ABSOLUTE_URL.test(raw)) {
      const url = new URL(raw);
      return `${url.origin}${normalizePathname(url.pathname, tenantSlug, slugInPath)}`;
    }
    return normalizePathname(stripped, tenantSlug, slugInPath);
  } catch {
    return normalizePathname(stripped.replace(ABSOLUTE_URL, '').replace(/^[^/]*/, ''), tenantSlug, slugInPath);
  }
}

// ------------------------------------------------------------
// before_send — o funil único de saída (D7 + D14 + D16)
// ------------------------------------------------------------

export interface BeforeSendContext {
  profile: PrivacyProfile;
  /** Lidos a cada evento: o slug muda em navegação path-based (task 6.3.1). */
  getTenantSlug: () => string | null;
  isSlugInPath: () => boolean;
}

/**
 * Propriedades de URL que saem pelo `before_send`, todas sem query string e
 * com os identificadores trocados por `:id`.
 *
 * Não são só a URL atual, o caminho e o referenciador. No primeiro evento da
 * visita, o posthog-js grava sozinho a URL, o caminho e o referenciador de
 * entrada da sessão (`$session_entry_*` nas propriedades do evento e, sem
 * prefixo, em `$set_once`) e os iniciais da pessoa (`$initial_*`, em
 * `$set_once`). Esse evento pode sair antes de a página tirar da barra de
 * endereço o `?ref=` do link da conversa ou o `?cupom=` do link da bio, e a
 * referência do link nunca pode chegar ao fornecedor. Por isso a lista vale
 * também dentro de `$set` e `$set_once`, nos dois perfis: normalizar URL não
 * tira dado de identificação do operador.
 */
const URL_PROPERTIES = [
  '$current_url',
  '$pathname',
  '$referrer',
  '$session_entry_url',
  '$session_entry_pathname',
  '$session_entry_referrer',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
  '$prev_pageview_pathname',
] as const;

/** Referenciador da visita direta: não é URL e segue como está. */
const DIRECT_REFERRER = '$direct';

/**
 * Cópia do objeto com as propriedades de URL normalizadas. Copia em vez de
 * alterar: no perfil `authenticated-b2b`, `$set` chega aqui sem cópia, e o
 * objeto pode ser o que a aplicação passou ao `identify`.
 */
function withNormalizedUrls<T>(record: T, tenantSlug: string | null, slugInPath: boolean): T {
  if (record === null || typeof record !== 'object' || Array.isArray(record)) return record;
  const source = record as Record<string, unknown>;
  let result: Record<string, unknown> | null = null;
  for (const key of URL_PROPERTIES) {
    const value = source[key];
    if (typeof value === 'string' && value && value !== DIRECT_REFERRER) {
      result ??= { ...source };
      result[key] = normalizeUrl(value, tenantSlug, slugInPath);
    }
  }
  return (result ?? record) as T;
}

export function buildBeforeSend(ctx: BeforeSendContext) {
  const preservePerson = ctx.profile === 'authenticated-b2b';

  return (event: CaptureResult | null): CaptureResult | null => {
    if (!event) return event;
    try {
      const tenantSlug = ctx.getTenantSlug();
      const slugInPath = ctx.isSlugInPath();
      if (event.properties) {
        const properties = withNormalizedUrls(
          scrubDeniedProperties(event.properties, preservePerson),
          tenantSlug,
          slugInPath,
        );
        for (const subtree of PERSON_SUBTREES) {
          if (properties[subtree] !== undefined) {
            properties[subtree] = withNormalizedUrls(properties[subtree], tenantSlug, slugInPath);
          }
        }
        if (event.event === '$snapshot') {
          blankRecordedInputs(properties['$snapshot_data']);
        }
        event.properties = properties;
        // Defesa em profundidade, não o mecanismo principal: o posthog-js
        // já descarta sessões automatizadas antes daqui (o filtro de user
        // agent vem ligado por padrão, `opt_out_useragent_filter: false`),
        // e mantemos esse filtro porque o cardápio é público e exposto a
        // crawlers. Esta marca só tem efeito se o filtro for desligado no
        // futuro, ou diante de automação que não se declare (D14).
        if (typeof navigator !== 'undefined' && navigator.webdriver) {
          event.properties['automation'] = true;
        }
      }
      if (event.$set) {
        event.$set = withNormalizedUrls(
          preservePerson ? event.$set : scrubDeniedProperties(event.$set, false),
          tenantSlug,
          slugInPath,
        );
      }
      if (event.$set_once) {
        event.$set_once = withNormalizedUrls(
          preservePerson ? event.$set_once : scrubDeniedProperties(event.$set_once, false),
          tenantSlug,
          slugInPath,
        );
      }
      return event;
    } catch (error) {
      // Falha na sanitização descarta o evento (fail closed): entre perder
      // um dado de produto e vazar um dado pessoal, perde-se o de produto.
      console.warn('[analytics] sanitização falhou; evento descartado', error);
      return null;
    }
  };
}

// ------------------------------------------------------------
// Session replay (D8)
// ------------------------------------------------------------

/**
 * URL gravada pelo replay, normalizada como a que o `before_send` transmite:
 * sem query string nem fragmento, e com os identificadores do caminho trocados
 * por `:id` (`normalizeUrl`). O SDK passa por aqui a URL da página (o `href`
 * dos eventos de meta, de troca de URL e de pageview do replay, em `{ name }`)
 * e a de cada requisição gravada. Sem isso, o `?ref=` do link da conversa, o
 * `?cupom=`, o token de um convite e o código do carrinho montado
 * (`/carrinho-pronto/{código}` e o `POST .../cart-handoffs/{código}/redeem`)
 * iriam ao fornecedor dentro da gravação, por fora das propriedades que o
 * `before_send` normaliza. A limpeza obrigatória do SDK roda antes desta.
 */
export function maskRecordedUrl(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return request.name ? { ...request, name: normalizeUrl(request.name, null, false) } : request;
}

/**
 * Máscaras por perfil. O mascaramento acontece no browser (rrweb): o
 * texto mascarado nunca é transmitido.
 *
 * - `public-anonymous`: `maskAllInputs` + `maskTextSelector: '*'` é a
 *   combinação de privacidade máxima do posthog-js (não existe opção
 *   `maskAllText` neste SDK — mascarar todo texto é papel do seletor).
 * - `authenticated-b2b`: inputs mascarados e `blockClass: 'ph-no-capture'`
 *   (o default do SDK, fixado aqui por contrato) aplicada aos blocos que
 *   exibem dados do consumidor final no painel.
 *
 * `recordHeaders`/`recordBody` ficam desligados nos dois perfis: o corpo
 * de POST /menu/{slug}/orders carrega o pedido inteiro, com nome,
 * telefone e endereço. E as URLs gravadas perdem a query e os identificadores
 * do caminho (`maskRecordedUrl`).
 */
export function buildSessionRecordingOptions(profile: PrivacyProfile) {
  const network = {
    recordHeaders: false,
    recordBody: false,
    maskCapturedNetworkRequestFn: maskRecordedUrl,
  } as const;
  if (profile === 'public-anonymous') {
    return { ...network, maskAllInputs: true, maskTextSelector: '*' };
  }
  return { ...network, maskAllInputs: true, blockClass: 'ph-no-capture' };
}

/**
 * O que a landing desliga na origem.
 *
 * A política de privacidade promete ao visitante do site institucional só as
 * páginas visitadas e a campanha de chegada; o README deste pacote, só
 * pageview e pageleave. Gravação de sessão, mapa de calor, clique morto,
 * pesquisa, erro e web vitals chegam LIGADOS pela configuração remota do
 * projeto — a mesma do painel e do cardápio —, e sem isto a v1.5.0 gravava a
 * navegação da landing, mascarada, sem que a política dissesse. Desligar aqui
 * vale para qualquer configuração que o projeto venha a ter.
 */
export const LANDING_PAGEVIEWS_ONLY = {
  disable_session_recording: true,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  disable_surveys: true,
  capture_exceptions: false,
  capture_performance: false,
} as const;
